// @flow
import { findIndex } from 'lodash/fp';
import { fromJS } from 'immutable';
import { handleActions } from 'redux-actions';

import { CLAIM_HUB } from '~/core/state/install-hub/constants';
import {
    BRIDGE_STATUS_UPDATE,
    PAIR_WITH_BRIDGE,
    SEARCH_FOR_BRIDGE,
    hueBridgeStatuses
} from '~/core/state/install-hue-bridge/constants';
import { CAMERA_SETTINGS_UPDATED } from '~/core/state/camera/constants';
import handleReduce from '~/core/state/handle-reduce';

import * as constants from './constants';
import type { StateType } from './types';
import { START_ACQUIRE_DEVICES, STOP_ACQUIRE_DEVICES } from '../acquire-devices/constants';
import type { Device } from '~/core/types/devices';

type CompareType = -1 | 0 | 1;
const stringCompare = (a, b): CompareType => new Map([[true, 0], [a > b, 1], [a < b, -1]]).get(true) || 0;

export const sortDevices = (
    { sortOrder, state: { name = '', zoneName = name } }: Device,
    { sortOrder: sortOrderB, state: { name: nameB = '', zoneName: zoneNameB = nameB } }: Device
) =>
    sortOrder === sortOrderB ? stringCompare(zoneName.toLowerCase(), zoneNameB.toLowerCase()) : sortOrder - sortOrderB;

const initialState: StateType = {
    deviceItems: [],
    hasError: false,
    hasRestartingError: false,
    isFetching: false,
    isRemoving: false,
    isLoaded: false,
    isRestarting: false
};

export default handleActions(
    {
        [START_ACQUIRE_DEVICES]: handleReduce(state => {
            const hubIndex = findIndex(x => x.type === 'hub', state.deviceItems);
            // $FlowFixMe
            return fromJS(state)
                .setIn(['deviceItems', hubIndex, 'state', 'discovery'], true)
                .toJS();
        }),
        [STOP_ACQUIRE_DEVICES]: handleReduce(state => {
            const hubIndex = findIndex(x => x.type === 'hub', state.deviceItems);
            // $FlowFixMe
            return fromJS(state)
                .setIn(['deviceItems', hubIndex, 'state', 'discovery'], false)
                .toJS();
        }),
        [CLAIM_HUB]: handleReduce((state, { payload }) => ({ ...state, deviceItems: [...state.deviceItems, payload] })),
        [BRIDGE_STATUS_UPDATE]: handleReduce((state, { payload }) => {
            const bridgeIndex = findIndex(x => x.id === payload.id, state.deviceItems);
            if (bridgeIndex < 0) {
                // $FlowFixMe
                return fromJS(state)
                    .updateIn(['deviceItems'], items => items.push(payload))
                    .toJS();
            }
            // $FlowFixMe
            return fromJS(state)
                .setIn(['deviceItems', bridgeIndex], payload)
                .toJS();
        }),
        [PAIR_WITH_BRIDGE]: handleReduce((state, { payload: { bridgeId, currentStatus } }) => {
            const bridgeIndex = findIndex(x => x.id === bridgeId, state.deviceItems);
            // $FlowFixMe
            return fromJS(state)
                .setIn(['deviceItems', bridgeIndex, 'props', 'status'], currentStatus)
                .toJS();
        }),
        [SEARCH_FOR_BRIDGE]: handleReduce((state, { payload: { bridgeId } }) => {
            const bridgeIndex = bridgeId ? findIndex(x => x.id === bridgeId, state.deviceItems) : -1;
            if (bridgeIndex < 0) {
                return state;
            }
            // $FlowFixMe
            return fromJS(state)
                .setIn(['deviceItems', bridgeIndex, 'props', 'status'], hueBridgeStatuses.SEARCHING)
                .toJS();
        }),
        [CAMERA_SETTINGS_UPDATED]: handleReduce((state, { payload: { cameraId, data } }) => {
            const cameraIndex = findIndex(x => x.props.hardwareIdentifier === cameraId, state.deviceItems);
            if (cameraIndex < 0) {
                return state;
            }
            // $FlowFixMe
            return fromJS(state)
                .mergeIn(['deviceItems', cameraIndex, 'state'], data)
                .toJS();
        }),
        [constants.REQUEST_DEVICES]: handleReduce(state => ({ ...state, isFetching: true })),
        [constants.RECEIVE_DEVICES]: handleReduce(
            (state, { payload }) => ({
                ...state,
                isFetching: false,
                hasError: false,
                isLoaded: true,
                deviceItems: payload
            }),
            state => ({
                ...state,
                isFetching: false,
                hasError: true,
                isLoaded: true
            })
        ),
        [constants.RENAME_DEVICE]: handleReduce(state => ({ ...state, saving: true })),
        [constants.RENAMED_DEVICE]: handleReduce(
            (state, { payload: { productId, name } }) => {
                const index = findIndex(x => x.id === productId, state.deviceItems);
                // $FlowFixMe
                const updated = fromJS(state)
                    .setIn(['deviceItems', index, 'state', 'name'], name)
                    .set('saving', false)
                    .toJS();

                return {
                    ...updated,
                    deviceItems: updated.deviceItems.sort(sortDevices)
                };
            },
            state => state.set('saving', false)
        ),
        [constants.REMOVE_DEVICE]: handleReduce(state => ({ ...state, isRemoving: true })),
        [constants.REMOVE_DEVICE_ERROR]: handleReduce(state => ({ ...state, hasError: true, isRemoving: false })),
        [constants.REMOVED_DEVICE]: handleReduce((state, { payload: { id } }) => ({
            ...state,
            deviceItems: state.deviceItems.filter(d => d.id !== id && d.parent !== id),
            isRemoving: false
        })),
        [constants.RENAME_ZONE]: handleReduce(state => ({ ...state, saving: true })),
        [constants.RENAMED_ZONE]: handleReduce((state, { payload: { productId, name } }) => {
            const index = findIndex(x => x.id === productId, state.deviceItems);
            const sibling = findIndex(x => {
                return x.id === (state.deviceItems[index].props.tui || state.deviceItems[index].props.zone);
            }, state.deviceItems);
            // $FlowFixMe
            let nextState = fromJS(state)
                .setIn(['deviceItems', index, 'state', 'name'], name)
                .setIn(['deviceItems', index, 'state', 'zoneName'], name);

            if (sibling) {
                nextState = nextState
                    .setIn(['deviceItems', sibling, 'state', 'name'], name)
                    .setIn(['deviceItems', sibling, 'state', 'zoneName'], name);
            }

            return nextState.set('saving', false).toJS();
        }),
        [constants.REMOVE_ZONE]: handleReduce(state => ({ ...state, isRemoving: true })),
        [constants.REMOVED_ZONE]: handleReduce((state, { payload: { id } }) => {
            const index = findIndex(x => x.id === id, state.deviceItems); //change deviceItems to state.deviceItems
            const sameZone = state.deviceItems[index].props.zone;
            // $FlowFixMe
            const result = fromJS(state)
                .deleteIn(['deviceItems', index])
                .set('isRemoving', false)
                .toJS();
            const sibling = findIndex(x => x.props.zone === sameZone, result.deviceItems);
            if (sibling) {
                // $FlowFixMe
                return fromJS(result)
                    .deleteIn(['deviceItems', sibling])
                    .toJS();
            }

            return result;
        }),
        [constants.RESTART_HUB]: handleReduce(state => ({ ...state, isRestarting: true })),
        [constants.RESTARTED_HUB]: handleReduce(
            (state, { payload }) => ({
                ...state,
                hasRestartingError: false,
                isRestarting: false
            }),
            state => ({
                ...state,
                hasRestartingError: true,
                isRestarting: false
            })
        )
    },
    initialState
);



// WEBPACK FOOTER //
// ./src/core/state/devices/index.js